import React, { useEffect, useState } from "react";
import AdminLayout from "../layout/AdminLayout";
import Header from "../components/Header";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  clearTrialMessages,
} from "../redux/reducers/trialSlice";
import { FiPlus, FiTrash2, FiEdit2, FiToggleLeft, FiToggleRight } from "react-icons/fi";

const typeColors = {
  info: "bg-blue-100 text-blue-700",
  warning: "bg-yellow-100 text-yellow-700",
  update: "bg-purple-100 text-purple-700",
  promotion: "bg-green-100 text-green-700",
};

const AnnouncementsPage = () => {
  const dispatch = useDispatch();
  const { announcements, loading, successMessage, error } = useSelector((state) => state.trial);

  const [form, setForm] = useState({ title: "", message: "", type: "info" });
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    dispatch(fetchAnnouncements());
  }, [dispatch]);

  useEffect(() => {
    if (successMessage || error) {
      const t = setTimeout(() => dispatch(clearTrialMessages()), 4000);
      return () => clearTimeout(t);
    }
  }, [successMessage, error, dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editId) {
      dispatch(updateAnnouncement({ id: editId, ...form }));
    } else {
      dispatch(createAnnouncement(form));
    }
    setForm({ title: "", message: "", type: "info" });
    setEditId(null);
    setShowForm(false);
  };

  const handleEdit = (ann) => {
    setForm({ title: ann.title, message: ann.message, type: ann.type });
    setEditId(ann._id);
    setShowForm(true);
  };

  const handleToggleActive = (ann) => {
    dispatch(updateAnnouncement({ id: ann._id, title: ann.title, message: ann.message, type: ann.type, active: !ann.active }));
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this announcement?")) {
      dispatch(deleteAnnouncement(id));
    }
  };

  return (
    <AdminLayout>
      <div className="p-4">
        <Header
          title="Announcements"
          subTitle="Admin"
          description="Create and manage platform-wide announcements shown to all logged-in users."
        />

        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm">
            ✅ {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>
        )}

        <div className="flex justify-end mb-4">
          <button
            onClick={() => { setShowForm(!showForm); setEditId(null); setForm({ title: "", message: "", type: "info" }); }}
            className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-5 py-2 rounded-xl text-sm font-semibold"
          >
            <FiPlus /> New Announcement
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <fieldset className="border-2 rounded-2xl px-6 py-5 mb-6">
            <legend className="font-bold px-1">{editId ? "Edit Announcement" : "Create Announcement"}</legend>
            <form onSubmit={handleSubmit} className="space-y-4 mt-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                    required
                    placeholder="e.g. New Feature Available"
                    className="w-full border-2 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                    className="w-full border-2 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-500"
                  >
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="update">Update</option>
                    <option value="promotion">Promotion</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                  required
                  rows={3}
                  placeholder="Write your announcement message..."
                  className="w-full border-2 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-purple-500 resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-xl text-sm font-semibold"
                >
                  {editId ? "Update" : "Publish"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditId(null); }}
                  className="border-2 px-6 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </fieldset>
        )}

        {/* List */}
        {loading ? (
          <p className="text-gray-400 text-sm text-center py-6">Loading announcements...</p>
        ) : announcements.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-2xl">
            <p className="text-gray-400 text-sm">No announcements yet. Create your first one above.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {announcements.map((ann) => (
              <div key={ann._id} className={`border-2 rounded-2xl p-5 ${ann.active ? "bg-white" : "bg-gray-50 opacity-60"}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${typeColors[ann.type]}`}>
                        {ann.type}
                      </span>
                      {!ann.active && (
                        <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">Inactive</span>
                      )}
                      <h3 className="font-semibold text-gray-800 text-sm">{ann.title}</h3>
                    </div>
                    <p className="text-gray-500 text-sm">{ann.message}</p>
                    <p className="text-gray-400 text-xs mt-2">
                      Created {new Date(ann.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleToggleActive(ann)}
                      title={ann.active ? "Deactivate" : "Activate"}
                      className="text-gray-400 hover:text-purple-600 transition-colors"
                    >
                      {ann.active ? <FiToggleRight className="text-xl text-purple-500" /> : <FiToggleLeft className="text-xl" />}
                    </button>
                    <button onClick={() => handleEdit(ann)} className="text-gray-400 hover:text-blue-600 transition-colors">
                      <FiEdit2 />
                    </button>
                    <button onClick={() => handleDelete(ann._id)} className="text-gray-400 hover:text-red-600 transition-colors">
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AnnouncementsPage;
